/**
 * BenefitSection — '혜택안내' 섹션 (상세페이지 + 지도 '정보 보기' 패널 공용).
 *
 * 순수 프레젠테이션. 데이터 가공은 domain/property/sectionAdapters.buildBenefitView.
 * v2i(2026-09-22) — 목업 04 상세 '혜택안내' 블록 그대로:
 *   - 혜택 1건 = 카드 1장(흰 카드 · radius 16 · p-4 · 그림자). 아이콘(kind, 오렌지 22px) + 제목(16/600)
 *     + 우측 배지(대표혜택 osolid | 할인율 ylw(내림) | 금액 green) + 보조줄(유형 · 종류 · 설명, 13px muted)
 *   - 대표혜택(headlineId) 카드는 오렌지 테두리(accent-line) + '대표혜택' 배지, 맨 앞
 *   - 섹션 머리 우측: 가장 이른 종료일 'YYYY-MM-DD까지'(b-mute) — valid_until 이 있을 때만
 *   - layout='grid'(상세 전용): lg 이상 3열, 카드 안은 세로(아이콘+배지 → 제목 → 설명).
 *     layout='stack'(기본 — 지도 패널처럼 폭이 좁은 곳): 항상 1열 가로 카드. 뷰포트가 아니라
 *     호출부가 넘기는 layout 으로 분기해 좁은 패널이 PC 뷰포트에서 3열로 깨지지 않게 한다.
 *   - 외부 링크·전화 CTA 없음(목업에 없음 — 전화는 상세 스티키 CTA·PC 우측 카드·패널 하단 바가 담당).
 */

import React from 'react';
import type { BenefitItem, BenefitView } from '../../../domain/property/sectionAdapters';
import { SectionCard } from './SectionCard';
import { CustomProse } from './CustomProse';
import { Badge } from '../Badge';
import { GiftIcon } from './SectionIcons';

export type BenefitLayout = 'stack' | 'grid';

export interface BenefitSectionProps {
  title?: string;
  view: BenefitView;
  /** 대표혜택 id — property.benefit_headline.benefit_id (오렌지 테두리 + '대표혜택' 배지) */
  headlineId?: number | null;
  /** 'grid' = 상세(lg 3열) · 'stack' = 좁은 패널(1열). 기본 stack */
  layout?: BenefitLayout;
  /** 잠긴 현장(비로그인 티저)에서 보여 줄 대표혜택 문구 — property.benefit_headline.text */
  lockedHeadline?: string | null;
}

/** 로그인 화면에 띄울 '이 현장 혜택 안내 요청' — 로그인하고 돌아오면 상세가 자동으로 요청한다(2026-10-08 대표 '추천안') */
export const BENEFIT_INTENT_KEY = 'benefit_request_intent';
export interface BenefitIntent {
  id: number;
  title: string;
  /** 로그인 화면에서 안내를 보고 로그인 버튼을 눌렀으면 true */
  ack?: boolean;
}

/** 혜택 잠금 해제 — 로그인 후 이 현장으로 돌아오게 저장하고 로그인 화면으로 (AuthContext 흐름 재사용) */
export function startBenefitLogin(propertyId?: number, propertyTitle?: string) {
  try {
    localStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
    if (propertyId) {
      const intent: BenefitIntent = { id: propertyId, title: propertyTitle || '' };
      localStorage.setItem(BENEFIT_INTENT_KEY, JSON.stringify(intent));
    }
  } catch {
    /* 저장 실패해도 로그인은 진행 — 돌아올 곳만 홈이 된다 */
  }
  window.location.assign('/login?reason=benefit');
}

/** 이 현장 혜택 안내 요청(= 이 현장 상담 연락 동의) → 서버 기록 뒤 화면 갱신.
 *  상세페이지가 'voonang:benefit-requested' 를 받아 조용히 다시 불러온다(preventDefault). 받는 곳이 없으면(지도 패널 등) 새로고침. */
export async function requestBenefit(propertyId: number): Promise<boolean> {
  try {
    const { propertyApi } = await import('../../../services/api');
    await propertyApi.recordBenefitLead(propertyId);
    propertyApi.clearCache(); // 목록·지도 캐시에 요청 전(잠긴) 혜택이 남지 않게
  } catch {
    return false;
  }
  const ev = new CustomEvent('voonang:benefit-requested', { detail: { id: propertyId }, cancelable: true });
  if (window.dispatchEvent(ev)) window.location.reload();
  return true;
}

/** 잠금 티저 — 2026-10-01 비로그인(상담 가능 현장 혜택은 카카오 로그인 뒤에)
 *  2026-10-08 대표 '추천안': 로그인 회원도 그 현장 혜택 안내를 요청(상담 연락 동의)해야 보인다. 광고성 정보 동의와는 무관. */
function LockedBenefits({
  count,
  headline,
  grid,
  reason = 'login',
  propertyId,
  propertyTitle,
}: {
  count: number;
  headline?: string | null;
  grid: boolean;
  reason?: 'login' | 'request';
  propertyId?: number;
  propertyTitle?: string;
}) {
  const request = reason === 'request';
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const onRequest = async () => {
    if (!propertyId || busy) return;
    setBusy(true);
    setFailed(false);
    const ok = await requestBenefit(propertyId);
    setBusy(false);
    if (!ok) setFailed(true);
  };
  return (
    <div
      data-benefit-locked=""
      className={`flex flex-col gap-3 rounded-ca-3xl border border-accent-line bg-frame p-4 shadow-ca-card ${grid ? 'lg:col-span-3' : ''}`}
    >
      {headline && (
        <span>
          <Badge tone="osolid" size="tag" ariaLabel="대표혜택">
            {headline}
          </Badge>
        </span>
      )}
      <div className="flex flex-col gap-0.5">
        <b className="break-keep text-[16px] font-semibold leading-[22px] text-ink">
          이 현장 혜택 {count}개가 준비돼 있어요
        </b>
        <span className="break-keep text-[13px] leading-[18px] text-muted">
          {request
            ? '혜택 안내를 요청하면 할인·납부 조건을 바로 보여 드려요. 이 현장 상담을 위해 담당자가 연락드릴 수 있어요.'
            : '카카오 로그인 후 할인·납부 조건을 모두 확인할 수 있어요'}
        </span>
      </div>
      <div aria-hidden className="flex select-none flex-col gap-1.5 blur-[3px]">
        {Array.from({ length: Math.min(Math.max(count, 2), 3) }).map((_, i) => (
          <div key={i} className="flex items-center justify-between rounded-ca-lg bg-ca-sunk px-3 py-2.5">
            <span className="text-[13px] text-body">{['분양가 할인 조건 안내', '계약금·중도금 납부 혜택', '추가 옵션 무상 제공'][i]}</span>
            <span className="text-[12px] font-bold text-accent">●●%</span>
          </div>
        ))}
      </div>
      {request ? (
        <>
          <button
            type="button"
            onClick={onRequest}
            disabled={busy}
            data-testid="benefit-request-cta"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-ca-xl bg-accent text-[15px] font-bold text-white active:opacity-80 disabled:opacity-60"
          >
            {busy ? '불러오는 중…' : '이 현장 혜택 보기'}
          </button>
          {failed && (
            <span role="alert" className="text-center text-[12px] text-bad">
              잠시 후 다시 눌러 주세요.
            </span>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => startBenefitLogin(propertyId, propertyTitle)}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-ca-xl bg-[#FEE500] text-[15px] font-bold text-[#191919] active:opacity-80"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="#191919">
            <path d="M12 3C6.5 3 2 6.6 2 11c0 2.8 1.9 5.3 4.7 6.7l-1 3.7c-.1.3.3.6.6.4l4.4-2.9c.4 0 .9.1 1.3.1 5.5 0 10-3.6 10-8S17.5 3 12 3z" />
          </svg>
          카카오로 3초 로그인하고 혜택 보기
        </button>
      )}
    </div>
  );
}

const icon = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** payments — 계약·납부조건 · 현금 */
const PaymentsIcon = () => (
  <svg {...icon}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 9.5v5M18 9.5v5" />
  </svg>
);
/** savings — 분양가 인하 */
const SavingsIcon = () => (
  <svg {...icon}>
    <path d="M19 10.5c0-3-3.1-5-7-5s-7.5 2.2-7.5 5.5c0 1.9 1 3.4 2.5 4.4V19h3v-2h4v2h3v-3.2c1-.6 1.7-1.4 2.1-2.3H21v-3h-2z" />
    <path d="M15.5 10h.01M9 8.5h4" />
  </svg>
);
/** verified_user — 보장 */
const ShieldIcon = () => (
  <svg {...icon}>
    <path d="M12 3l7.5 3v5.5c0 4.5-3.2 8.1-7.5 9.5-4.3-1.4-7.5-5-7.5-9.5V6L12 3z" />
    <path d="M8.8 12.2l2.2 2.2 4.2-4.4" />
  </svg>
);
/** receipt — 세제 */
const ReceiptIcon = () => (
  <svg {...icon}>
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </svg>
);
/** sell — 기타 조건 */
const TagIcon = () => (
  <svg {...icon}>
    <path d="M3 12V4h8l10 10-8 8L3 12z" />
    <path d="M7.5 7.5h.01" />
  </svg>
);

function KindIcon({ kind }: { kind: string }) {
  switch (kind) {
    case 'price_cut':
      return <SavingsIcon />;
    case 'payment':
    case 'cashback':
      return <PaymentsIcon />;
    case 'free_option':
      return <GiftIcon />;
    case 'guarantee':
      return <ShieldIcon />;
    case 'tax':
      return <ReceiptIcon />;
    default:
      return <TagIcon />;
  }
}

function CardBadge({ item, isHead }: { item: BenefitItem; isHead: boolean }) {
  if (isHead) {
    return (
      <Badge tone="osolid" size="tag" ariaLabel="대표혜택">
        대표혜택
      </Badge>
    );
  }
  if (!item.valueBadge) return null;
  return (
    <Badge tone={item.valueBadge.tone} size="tag" className="num">
      {item.valueBadge.text}
    </Badge>
  );
}

/** 보조줄 — 목업 '계약·납부조건 · 초기 자금 …'. 설명이 HTML 이면 줄 아래에 정화 렌더. */
function MetaLine({ item }: { item: BenefitItem }) {
  const isHtml = /<[a-z][\s\S]*>/i.test(item.description);
  const labels = [item.unitLabel, item.kindLabel].filter(Boolean) as string[];
  const inline = [...labels, !isHtml && item.description ? item.description : ''].filter(Boolean);
  return (
    <>
      {inline.length > 0 && (
        <span className="break-keep text-[13px] leading-[18px] text-muted">{inline.join(' · ')}</span>
      )}
      {isHtml && (
        <CustomProse
          html={item.description}
          className="break-keep !text-[13px] !leading-[18px] !text-muted [&_li]:text-muted [&_p+p]:mt-1 [&_p]:text-muted"
        />
      )}
      {item.until && (
        <span className={`text-[12px] font-bold ${item.untilSoon ? 'text-bad' : 'text-muted'}`}>{item.until}</span>
      )}
    </>
  );
}

export function BenefitSection({
  title = '혜택안내',
  view,
  headlineId = null,
  layout = 'stack',
  lockedHeadline = null,
}: BenefitSectionProps) {
  const grid = layout === 'grid';
  const action = view.untilDate ? (
    <Badge tone="mute" className="num">
      {view.untilDate}까지
    </Badge>
  ) : undefined;

  return (
    <SectionCard title={title} icon={<GiftIcon />} action={action}>
      <div
        className={grid ? 'grid grid-cols-1 gap-2 lg:grid-cols-3 lg:gap-3' : 'flex flex-col gap-2'}
        data-benefit-layout={layout}
      >
        {view.items.map((item) => {
          const isHead = headlineId != null && item.id === headlineId;
          return (
            <div
              key={item.id}
              data-benefit-card=""
              data-headline={isHead ? 'true' : undefined}
              className={`flex gap-3 rounded-ca-3xl border bg-frame p-4 shadow-ca-card ${
                isHead ? 'border-accent-line' : 'border-ca-line'
              } ${grid ? 'lg:flex-col lg:gap-1.5' : ''}`}
            >
              <div className={`flex shrink-0 items-center text-accent ${grid ? 'lg:justify-between' : ''}`}>
                <KindIcon kind={item.kind} />
                {grid && (
                  <span className="hidden lg:inline-flex">
                    <CardBadge item={item} isHead={isHead} />
                  </span>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-start justify-between gap-2">
                  <b className="break-keep text-[16px] font-semibold leading-[22px] tracking-[-0.01em] text-ink">
                    {item.title}
                  </b>
                  <span className={`shrink-0 pt-[2px] ${grid ? 'lg:hidden' : ''}`}>
                    <CardBadge item={item} isHead={isHead} />
                  </span>
                </div>
                <MetaLine item={item} />
              </div>
            </div>
          );
        })}

        {view.restricted && (
          <LockedBenefits
            count={view.restricted.count}
            headline={lockedHeadline}
            grid={grid}
            reason={view.restricted.reason}
            propertyId={view.restricted.propertyId}
            propertyTitle={view.restricted.propertyTitle}
          />
        )}
      </div>
    </SectionCard>
  );
}

export default BenefitSection;
